/**
 * RemotePhotoRequest
 *
 * Componente riusabile per richiedere foto remote agli operatori mobile.
 * Include:
 * - Lista operatori disponibili
 * - Selezione operatore specifico o broadcast a tutti
 * - Tracking stato richiesta (attesa, accettata, rifiutata)
 * - Chat integrata con operatore
 * - WebSocket via useSharedWebSocket
 *
 * @author Salvo de Luca - Dialogicom s.r.l.s.
 * @version 1.0.0
 * @date Gennaio 2026
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { appConfirm } from '../../lib/appDialog';
import {
  Smartphone,
  Users,
  Send,
  Radio,
  User,
  Loader2,
  RefreshCw,
  X,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  Wifi,
  Bell
} from 'lucide-react';
import { useSharedWebSocket, sendSharedMessage, registerMessageHandler } from '../../hooks/useSharedWebSocket';
import { markOrphanRequest } from './orphanPhotoRequests';

// ============================================================================
// TYPES
// ============================================================================

export interface RemotePhotoResult {
  blob: Blob;
  dataUrl: string;
  sourceUrl?: string; // URL originale della foto (già caricata dal mobile)
}

export interface RemotePhotoRequestProps {
  /** Callback quando arriva la foto */
  onPhotoReceived: (photo: RemotePhotoResult) => void;

  /** Callback per chiusura/annullamento */
  onCancel: () => void;

  /** Tipo entità per la richiesta (default: 'generic') */
  entityType?: string;

  /** ID entità (default: 'new') */
  entityId?: string;

  /** Titolo richiesta mostrato all'operatore */
  title?: string;

  /** Descrizione richiesta mostrata all'operatore */
  description?: string;

  /** ID utente desktop corrente */
  userId: string;

  /** Nome utente desktop corrente */
  userName: string;

  /** Mostrare suggerimento chat (default: true) */
  showChatHint?: boolean;

  /** Callback navigazione a gestione operatori (opzionale) */
  onNavigateToOperators?: () => void;

  /** Quando true, l'operatore mobile registra un VIDEO invece di scattare foto. Default false (retro-compatibile). */
  allowVideo?: boolean;
  /** Durata massima video in secondi (solo se allowVideo=true). Default 60. */
  maxVideoSeconds?: number;
}

interface MobileOperator {
  id: number;
  username: string;
  displayName: string;
  role: string;
  active: boolean;
  lastLogin: string | null;
  userId?: string;
  userName?: string;
  status?: 'standby' | 'busy';
  connectionStatus?: 'online' | 'offline'; // ✅ Online = WebSocket, Offline = solo push
  wsConnected?: boolean; // true = foreground (WS alive), false = background (push only)
  lastActivity?: string; // ISO string dell'ultima attività
}

interface ChatMessage {
  id: string;
  from: 'desktop' | 'mobile';
  fromName: string;
  message: string;
  timestamp: Date;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const RemotePhotoRequest: React.FC<RemotePhotoRequestProps> = ({
  onPhotoReceived,
  onCancel,
  entityType = 'generic',
  entityId = 'new',
  title = 'Richiesta Foto',
  description = 'Scatta una foto',
  userId,
  userName,
  showChatHint = true,
  onNavigateToOperators,
  allowVideo = false,
  maxVideoSeconds = 60,
}) => {
  // ========================
  // STATE
  // ========================

  const [availableOperators, setAvailableOperators] = useState<MobileOperator[]>([]);
  const [isLoadingOperators, setIsLoadingOperators] = useState(true);
  const [_selectedOperatorId, setSelectedOperatorId] = useState<string | null>(null);
  const [_requestId, setRequestId] = useState<string | null>(null);
  const [isWaitingForPhoto, setIsWaitingForPhoto] = useState(false);
  const [acceptedByOperator, setAcceptedByOperator] = useState<string | null>(null);
  const [_acceptedByOperatorId, setAcceptedByOperatorId] = useState<string | null>(null);
  const [refusedByOperator, setRefusedByOperator] = useState<string | null>(null);
  const [_chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string>('');

  // Refs per evitare closure stale
  const currentRequestIdRef = useRef<string | null>(null);
  const unregisterHandlersRef = useRef<(() => void)[]>([]);
  const photoReceivedRef = useRef<boolean>(false); // ✅ Previene chiamate multiple onPhotoReceived
  // Dedup: il backend manda sia `photo_uploaded` (per ogni foto della session)
  // sia `request_completed_by_mobile` (al completamento). Senza questo set la
  // stessa foto veniva propagata due volte al parent → 2 foto identiche sul PC.
  const processedPhotoUrlsRef = useRef<Set<string>>(new Set());
  // Snapshot per il cleanup di unmount: serve a sapere se al momento dello
  // smontaggio del componente eravamo in attesa di una foto da un operatore
  // che aveva già accettato. In quel caso marchiamo la richiesta come orfana
  // così che OrphanPhotoWatcher (montato in App.tsx) la intercetti.
  const acceptedOperatorRef = useRef<string | null>(null);
  const requestTitleRef = useRef<string>('');

  // Unmount: se eravamo in attesa di una foto già accettata e non ancora
  // arrivata, marchiamo la richiesta come orfana così OrphanPhotoWatcher
  // la intercetta e scarica il file. Copre anche il caso "utente naviga
  // via senza cliccare la X" (es. cambio di tab, chiusura modal dal parent).
  useEffect(() => {
    return () => {
      const reqId = currentRequestIdRef.current;
      if (!reqId) return;
      if (photoReceivedRef.current) return; // foto già processata, niente orfani
      if (!acceptedOperatorRef.current) return; // mai accettata, niente da recuperare
      markOrphanRequest(reqId, {
        title: requestTitleRef.current || 'Richiesta foto',
        from: acceptedOperatorRef.current,
      });
    };
  }, []);

  // ========================
  // WEBSOCKET
  // ========================

  // WebSocket condiviso - sempre attivo quando userId presente
  const { isConnected: wsConnected } = useSharedWebSocket({
    userId,
    userName,
    userType: 'desktop',
    enabled: !!userId
  });

  // ========================
  // HELPERS
  // ========================

  const generateRequestId = useCallback(() => {
    return `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Fetch foto da URL e converti in blob + dataUrl
  const fetchPhotoAsBlob = useCallback(async (photoUrl: string): Promise<RemotePhotoResult> => {
    const fullUrl = photoUrl.startsWith('/')
      ? `${window.location.origin}${photoUrl}`
      : photoUrl;

    const response = await fetch(fullUrl);
    const blob = await response.blob();
    const dataUrl = URL.createObjectURL(blob);

    return { blob, dataUrl, sourceUrl: fullUrl };
  }, []);

  // ========================
  // OPERATORS - SOLO WEBSOCKET REAL-TIME
  // ========================

  // La lista operatori viene popolata SOLO dal WebSocket (available_mobiles)
  // Non usiamo più l'API REST perché restituisce tutti gli operatori "active" dal DB
  // invece di quelli realmente connessi via WebSocket

  // Funzione per richiedere refresh lista operatori via WebSocket
  const requestOperatorsList = useCallback(() => {
    console.log('[RemotePhotoRequest] Richiesta refresh lista operatori...');
    setIsLoadingOperators(true);

    // Invia messaggio al server per richiedere la lista aggiornata
    const sent = sendSharedMessage({
      type: 'get_available_mobiles'
    });

    if (!sent) {
      console.log('[RemotePhotoRequest] WebSocket non connesso, impossibile richiedere lista');
      setError('WebSocket non connesso. Riprova tra qualche secondo.');
      setIsLoadingOperators(false);
    }
  }, []);

  // Timeout per mostrare messaggio se WebSocket non risponde
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoadingOperators && availableOperators.length === 0) {
        console.log('[RemotePhotoRequest] Timeout attesa WebSocket, mostro lista vuota');
        setIsLoadingOperators(false);
      }
    }, 5000); // 5 secondi timeout

    return () => clearTimeout(timeout);
  }, [isLoadingOperators, availableOperators.length]);

  // ========================
  // WEBSOCKET OPERATORS REALTIME
  // ========================

  // Sottoscrivi agli aggiornamenti real-time degli operatori via WebSocket
  useEffect(() => {
    console.log('[RemotePhotoRequest] Registrazione handler available_mobiles...');

    const handleAvailableMobiles = (data: any) => {
      console.log('[RemotePhotoRequest] available_mobiles ricevuto:', data);
      if (data.mobiles && Array.isArray(data.mobiles)) {
        // ✅ Includi TUTTI gli operatori (online via WebSocket + offline con push)
        // Gli operatori offline possono ricevere richieste via push notification
        const allOperators = data.mobiles
          .map((m: any) => ({
            id: parseInt(m.userId) || 0,
            username: m.userName,
            displayName: m.userName,
            role: 'mobile_operator',
            active: true,
            lastLogin: m.lastSeen || null,
            userId: m.userId,
            userName: m.userName,
            status: 'standby' as const,
            // `m.status` è la DISPONIBILITÀ ('standby' | 'busy'), non la connessione:
            // leggerla qui rendeva `connectionStatus` sempre 'standby', quindi mai
            // 'online', quindi pallino grigio per tutti e contatori a zero. La
            // raggiungibilità la dice `connectionStatus` (o `wsConnected` sui
            // backend più vecchi).
            connectionStatus: (m.connectionStatus as 'online' | 'offline')
              ?? (m.wsConnected === false ? 'offline' : 'online'),
            wsConnected: m.wsConnected ?? true, // true = foreground, false = background
            lastActivity: m.lastActivity || null // ISO string ultima attività
          }))
          // Ordina: online prima, poi offline
          .sort((a: MobileOperator, b: MobileOperator) => {
            if (a.connectionStatus === 'online' && b.connectionStatus !== 'online') return -1;
            if (a.connectionStatus !== 'online' && b.connectionStatus === 'online') return 1;
            return 0;
          });

        const onlineCount = allOperators.filter((o: MobileOperator) => o.connectionStatus === 'online').length;
        const offlineCount = allOperators.filter((o: MobileOperator) => o.connectionStatus === 'offline').length;
        console.log(`[RemotePhotoRequest] Operatori: ${onlineCount} online, ${offlineCount} offline (push)`,
          allOperators.map((o: MobileOperator) => `${o.userName} (${o.connectionStatus})`));
        setAvailableOperators(allOperators);
        setIsLoadingOperators(false);
      }
    };

    const unregister = registerMessageHandler('available_mobiles', handleAvailableMobiles);

    return () => {
      unregister();
    };
  }, []);

  // ✅ Richiedi lista operatori automaticamente quando WebSocket si connette
  useEffect(() => {
    if (wsConnected) {
      console.log('[RemotePhotoRequest] WebSocket connesso, richiedo lista operatori...');
      // Piccolo delay per assicurarsi che l'auth sia completato
      const timer = setTimeout(() => {
        requestOperatorsList();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [wsConnected, requestOperatorsList]);

  // ========================
  // PHOTO REQUEST HANDLERS
  // ========================

  // Registra handler per foto al mount
  useEffect(() => {
    console.log('[RemotePhotoRequest] Registrazione handler foto...');

    const deliverPhoto = async (url: string) => {
      if (!url) return;
      if (processedPhotoUrlsRef.current.has(url)) {
        console.log('[RemotePhotoRequest] 🚫 Foto già consegnata, skip duplicato:', url);
        return;
      }
      processedPhotoUrlsRef.current.add(url);
      try {
        const photo = await fetchPhotoAsBlob(url);
        setIsWaitingForPhoto(false);
        photoReceivedRef.current = true; // disattiva il fallback orfano allo smontaggio
        onPhotoReceived(photo);
      } catch (err) {
        console.error('[RemotePhotoRequest] Errore caricamento foto:', err);
        setError('Errore nel caricamento della foto');
      }
    };

    const handlePhotoUploaded = async (data: any) => {
      console.log('[RemotePhotoRequest] photo_uploaded ricevuto:', data);
      if (data.url) await deliverPhoto(data.url);
    };

    const handleRequestCompleted = async (data: any) => {
      console.log('[RemotePhotoRequest] request_completed_by_mobile ricevuto:', data);
      const photos = data.photos || [];
      for (const p of photos) {
        const url = typeof p === 'string' ? p : (p?.url || '');
        if (url) await deliverPhoto(url);
      }
    };

    const unregister1 = registerMessageHandler('photo_uploaded', handlePhotoUploaded);
    const unregister2 = registerMessageHandler('request_completed_by_mobile', handleRequestCompleted);

    return () => {
      unregister1();
      unregister2();
    };
  }, [fetchPhotoAsBlob, onPhotoReceived]);

  // ========================
  // SEND REQUEST
  // ========================

  const sendPhotoRequest = useCallback((targetOperatorId: string | null) => {
    const newRequestId = generateRequestId();
    setRequestId(newRequestId);
    currentRequestIdRef.current = newRequestId;
    photoReceivedRef.current = false; // ✅ Reset flag per nuova richiesta
    processedPhotoUrlsRef.current.clear(); // ✅ Reset dedup foto per nuova richiesta
    acceptedOperatorRef.current = null;
    requestTitleRef.current = title || 'Richiesta foto';
    setIsWaitingForPhoto(true);
    setError('');

    // Pulisci handler precedenti
    unregisterHandlersRef.current.forEach(unregister => unregister());
    unregisterHandlersRef.current = [];

    // Handler per messaggi specifici della richiesta
    const handleMessages = (data: any) => {
      console.log('[RemotePhotoRequest] Received:', data.type, data);

      switch (data.type) {
        case 'request_assigned': {
          const operatorName = data.assignedTo?.userName || 'Operatore';
          const operatorId = data.assignedTo?.userId || null;
          setAcceptedByOperator(operatorName);
          setAcceptedByOperatorId(operatorId);
          acceptedOperatorRef.current = operatorName;

          const msg: ChatMessage = {
            id: `sys-${Date.now()}`,
            from: 'mobile',
            fromName: operatorName,
            message: '✅ Ho accettato la richiesta. Sto preparando la foto...',
            timestamp: new Date()
          };
          setChatMessages(prev => [...prev, msg]);
          break;
        }

        case 'request_refused_by_mobile': {
          const refuserName = data.refusedBy?.userName || 'Operatore';
          setRefusedByOperator(refuserName);
          break;
        }

        case 'chat_message': {
          if (data.requestId === currentRequestIdRef.current || data.targetUserId === userId) {
            const msg: ChatMessage = {
              id: `msg-${Date.now()}`,
              from: 'mobile',
              fromName: data.fromName || 'Operatore',
              message: data.message,
              timestamp: new Date()
            };
            setChatMessages(prev => [...prev, msg]);
          }
          break;
        }

        case 'dm_new_message': {
          if (data.senderId && data.content) {
            const msg: ChatMessage = {
              id: `dm-${Date.now()}`,
              from: 'mobile',
              fromName: data.senderName || 'Operatore',
              message: data.content,
              timestamp: new Date(data.timestamp || Date.now())
            };
            setChatMessages(prev => [...prev, msg]);
          }
          break;
        }

        case 'photo_request_timeout':
        case 'photo_request_cancelled': {
          setError('Nessun operatore ha risposto. Riprova.');
          setIsWaitingForPhoto(false);
          break;
        }
      }
    };

    // Registra handler
    const messageTypes = [
      'request_assigned',
      'request_refused_by_mobile',
      'chat_message',
      'dm_new_message',
      'photo_request_timeout',
      'photo_request_cancelled'
    ];

    messageTypes.forEach(type => {
      const unregister = registerMessageHandler(type, handleMessages);
      unregisterHandlersRef.current.push(unregister);
    });

    // Invia richiesta dopo breve delay
    setTimeout(() => {
      const sent = sendSharedMessage({
        type: 'create_request',
        title,
        description,
        entityType,
        entityId,
        targetUserId: targetOperatorId,
        mediaType: allowVideo ? 'video' : 'photo',
        maxSeconds: allowVideo ? maxVideoSeconds : undefined,
      });

      if (sent) {
        const systemMsg: ChatMessage = {
          id: `sys-${Date.now()}`,
          from: 'desktop',
          fromName: 'Sistema',
          message: targetOperatorId
            ? 'Richiesta foto inviata all\'operatore selezionato...'
            : 'Richiesta foto inviata a tutti gli operatori disponibili...',
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, systemMsg]);
      } else {
        setError('WebSocket non connesso. Riprova.');
        setIsWaitingForPhoto(false);
      }
    }, 300);
  }, [generateRequestId, title, description, entityType, entityId, userId, allowVideo, maxVideoSeconds]);

  // ========================
  // ACTIONS
  // ========================

  const handleSendToOperator = (operatorId: string) => {
    setSelectedOperatorId(operatorId);
    sendPhotoRequest(operatorId);
  };

  const handleBroadcastToAll = () => {
    setSelectedOperatorId(null);
    sendPhotoRequest(null);
  };

  const handleRetry = () => {
    setRefusedByOperator(null);
    setIsWaitingForPhoto(false);
    setAcceptedByOperator(null);
    setAcceptedByOperatorId(null);
    setChatMessages([]);
    photoReceivedRef.current = false; // ✅ Reset flag per nuova richiesta
    processedPhotoUrlsRef.current.clear(); // ✅ Reset dedup foto per nuova richiesta
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unregisterHandlersRef.current.forEach(unregister => unregister());
    };
  }, []);

  // ========================
  // RENDER
  // ========================

  return (
    <div className="bg-slate-800 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Smartphone size={24} className="text-green-400" />
          <div>
            <h2 className="text-xl font-semibold text-white">
              {isWaitingForPhoto ? 'In attesa della foto...' : 'Seleziona Operatore Mobile'}
            </h2>
            <p className="text-slate-400 text-sm">
              {isWaitingForPhoto
                ? acceptedByOperator
                  ? `${acceptedByOperator} sta preparando la foto`
                  : 'Attendo risposta dagli operatori...'
                : 'Scegli un operatore o invia a tutti'
              }
            </p>
          </div>
        </div>
        <button
          onClick={async () => {
            // Se l'operatore mobile ha già accettato la richiesta, chiudere
            // la modale smonta il listener `request_completed_by_mobile`.
            // Segnaliamo la richiesta come "orfana": OrphanPhotoWatcher
            // (montato in App.tsx) intercetterà la foto in arrivo e la
            // scaricherà automaticamente sul disco dell'utente.
            if (isWaitingForPhoto && acceptedByOperator && currentRequestIdRef.current) {
              const ok = await appConfirm(
                `${acceptedByOperator} ha accettato e sta scattando la foto.\n\n` +
                `Se chiudi, la foto verrà comunque scaricata automaticamente sul ` +
                `tuo PC appena arriva (vedrai una notifica).\n\nChiudere?`
              );
              if (!ok) return;
              markOrphanRequest(currentRequestIdRef.current, {
                title: title || 'Richiesta foto',
                from: acceptedByOperator,
              });
            }
            onCancel();
          }}
          className="text-slate-400 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Loading operators */}
      {isLoadingOperators && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="text-blue-400 animate-spin" />
          <span className="ml-3 text-slate-400">Caricamento operatori...</span>
        </div>
      )}

      {/* No operators */}
      {!isLoadingOperators && availableOperators.length === 0 && !isWaitingForPhoto && (
        <div className="text-center py-12">
          <Users size={48} className="mx-auto mb-4 text-slate-500" />
          <h3 className="text-lg font-medium text-white mb-2">
            Nessun operatore online
          </h3>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">
            Al momento non ci sono operatori mobile disponibili.
          </p>
          <div className="flex justify-center gap-4">
            {onNavigateToOperators && (
              <button
                onClick={onNavigateToOperators}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
              >
                <Users size={18} />
                Gestione Operatori
              </button>
            )}
            <button
              onClick={requestOperatorsList}
              className="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
            >
              <RefreshCw size={18} />
              Ricarica
            </button>
          </div>
        </div>
      )}

      {/* Operators list */}
      {!isLoadingOperators && availableOperators.length > 0 && !isWaitingForPhoto && (
        <div className="space-y-4">
          {/* Broadcast option */}
          <button
            onClick={handleBroadcastToAll}
            className="w-full flex items-center justify-between p-4 bg-green-600/20 border border-green-600 rounded-lg hover:bg-green-600/30 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                <Radio size={20} className="text-white" />
              </div>
              <div className="text-left">
                <p className="font-medium text-white">Invia a tutti</p>
                <p className="text-sm text-green-400">
                  {(() => {
                    const fgCount = availableOperators.filter(o => o.connectionStatus === 'online' && o.wsConnected !== false).length;
                    const bgCount = availableOperators.filter(o => o.connectionStatus === 'online' && o.wsConnected === false).length;
                    const offlineCount = availableOperators.filter(o => o.connectionStatus === 'offline').length;
                    const parts = [];
                    if (fgCount > 0) parts.push(`${fgCount} attivi`);
                    if (bgCount > 0) parts.push(`${bgCount} background`);
                    if (offlineCount > 0) parts.push(`${offlineCount} via push`);
                    return parts.join(', ') || `${availableOperators.length} disponibili`;
                  })()}
                </p>
              </div>
            </div>
            <Send size={20} className="text-green-400 group-hover:translate-x-1 transition-transform" />
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-600"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-slate-800 text-slate-400 text-sm">oppure seleziona</span>
            </div>
          </div>

          {/* Individual operators */}
          <div className="space-y-2">
            {availableOperators.map((operator) => {
              const isOnline = operator.connectionStatus === 'online';
              const isForeground = isOnline && operator.wsConnected !== false;
              const isBackground = isOnline && operator.wsConnected === false;
              // Calcola tempo dall'ultima attività
              const lastActivityText = (() => {
                if (!operator.lastActivity) return null;
                const diff = Date.now() - new Date(operator.lastActivity).getTime();
                if (diff < 0 || isNaN(diff)) return null;
                const minutes = Math.floor(diff / 60000);
                if (minutes < 1) return 'ora';
                if (minutes < 60) return `${minutes}m fa`;
                const hours = Math.floor(minutes / 60);
                if (hours < 24) return `${hours}h fa`;
                return new Date(operator.lastActivity).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
              })();
              return (
                <button
                  key={operator.userId}
                  onClick={() => handleSendToOperator(operator.userId!)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors group ${
                    isForeground
                      ? 'bg-slate-700/50 border border-slate-600 hover:bg-slate-700 hover:border-blue-500'
                      : isBackground
                        ? 'bg-slate-700/30 border border-slate-600 hover:bg-slate-700/50 hover:border-amber-500'
                        : 'bg-slate-800/50 border border-slate-700 hover:bg-slate-700/50 hover:border-amber-500'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar con indicatore stato */}
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isForeground ? 'bg-slate-600' : isBackground ? 'bg-slate-650' : 'bg-slate-700'
                      }`}>
                        <User size={20} className={isForeground ? 'text-slate-300' : 'text-slate-400'} />
                      </div>
                      {/* Badge stato connessione: verde=foreground, giallo=background, grigio=offline */}
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                        isForeground ? 'bg-green-500' : isBackground ? 'bg-amber-500' : 'bg-gray-500'
                      }`}>
                        {isForeground ? (
                          <Wifi size={10} className="text-white" />
                        ) : (
                          <Bell size={10} className="text-white" />
                        )}
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${isForeground ? 'text-white' : 'text-slate-300'}`}>
                          {operator.userName || operator.displayName}
                        </p>
                        {/* Label stato */}
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          isForeground
                            ? 'bg-green-500/20 text-green-400'
                            : isBackground
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {isForeground ? 'Foreground' : isBackground ? 'Background' : 'Push'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        {isForeground && (
                          <span className="text-green-400/70">App attiva{lastActivityText ? ` - ${lastActivityText}` : ''}</span>
                        )}
                        {isBackground && (
                          <span className="text-amber-400/70">In background{lastActivityText ? ` - ${lastActivityText}` : ''}</span>
                        )}
                        {!isOnline && (
                          <span className="text-gray-400/70">
                            Notifica push{lastActivityText ? ` - Ultimo: ${lastActivityText}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Send size={18} className={`transition-all group-hover:translate-x-1 ${
                    isForeground
                      ? 'text-slate-400 group-hover:text-blue-400'
                      : isBackground
                        ? 'text-slate-500 group-hover:text-amber-400'
                        : 'text-slate-500 group-hover:text-amber-400'
                  }`} />
                </button>
              );
            })}
          </div>

          {/* Nota per operatori offline */}
          {availableOperators.some(o => o.connectionStatus === 'offline') && (
            <p className="text-xs text-slate-500 text-center mt-2">
              💡 Gli operatori "Push" riceveranno una notifica sul telefono anche se l'app è in background
            </p>
          )}
        </div>
      )}

      {/* Waiting for photo - Status panel */}
      {isWaitingForPhoto && (
        <div className="bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            {refusedByOperator ? (
              <>
                <AlertCircle size={24} className="text-red-400" />
                <span className="text-red-400 font-medium">
                  {refusedByOperator} ha rifiutato la richiesta
                </span>
              </>
            ) : acceptedByOperator ? (
              <>
                <CheckCircle size={24} className="text-green-400" />
                <span className="text-green-400 font-medium">
                  {acceptedByOperator} ha accettato
                </span>
              </>
            ) : (
              <>
                <Loader2 size={24} className="text-blue-400 animate-spin" />
                <span className="text-blue-400">Attendo risposta...</span>
              </>
            )}
          </div>

          <p className="text-slate-400 text-sm">
            {refusedByOperator
              ? 'L\'operatore ha rifiutato la richiesta. Puoi inviare una nuova richiesta.'
              : acceptedByOperator
                ? 'L\'operatore sta scattando la foto. Riceverai l\'immagine automaticamente.'
                : 'La richiesta è stata inviata. Il primo operatore che accetta si occuperà della foto.'
            }
          </p>

          {/* Chat hint */}
          {showChatHint && (
            <div className="mt-4 pt-4 border-t border-slate-600">
              <p className="text-slate-500 text-xs flex items-center gap-2">
                <MessageCircle size={14} />
                Per comunicare con l'operatore, usa la chat in basso a destra
              </p>
            </div>
          )}

          {/* Retry button if refused */}
          {refusedByOperator && (
            <div className="mt-4">
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm"
              >
                Invia nuova richiesta
              </button>
            </div>
          )}
        </div>
      )}

      {/* WebSocket status indicator */}
      <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between text-xs">
        <span className="text-slate-500">
          WebSocket: {wsConnected ? 'Connesso' : 'Disconnesso'}
        </span>
        <span className={`flex items-center gap-1 ${wsConnected ? 'text-green-400' : 'text-red-400'}`}>
          <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400' : 'bg-red-400'}`}></span>
          {wsConnected ? 'Online' : 'Offline'}
        </span>
      </div>
    </div>
  );
};

export default RemotePhotoRequest;
